import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, IconButton, Typography, useMediaQuery, useTheme, Stack, Badge } from '@mui/material';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import AppsOutageIcon from '@mui/icons-material/AppsOutage';
import useUsers from '../../context/UsersContext';
import { getIcon } from '../../utils/helper';
import { sxConfig } from './otherActivityReportPage.style';
import otherActivityReportService from '../../service/otherActivityReport.service';
import ConfirmDialogV2UI from '../Interface/ConfirmDialogV2UI';

dayjs.extend(duration);

const OpenReportTimer = ({ startDate }) => {
  const [elapsedTime, setElapsedTime] = useState('');

  useEffect(() => {
    if (!startDate) {
      setElapsedTime('');
      return;
    }

    const updateTimer = () => {
      const diff = dayjs.duration(dayjs().diff(dayjs(startDate)));

      setElapsedTime(
        `${diff.hours().toString().padStart(2, '0')}:${diff.minutes().toString().padStart(2, '0')}:${diff.seconds().toString().padStart(2, '0')}`
      );
    };

    updateTimer();

    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [startDate]);

  return <Typography sx={sxConfig.openReportTimerText}>{elapsedTime}</Typography>;
};

export const OtherActivityReportPage = ({ dialog }) => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('md'));
  const { getUser, user } = useUsers();
  const [activities, setActivities] = useState([]);

  const userId = dialog ? dialog?.dialogData?.userId : user.userId;

  const defaultConfirmDialogData = {
    open: false,
    message: '',
    buttons: [
      {
        label: 'Megerősít',
        variant: 'outlined',
        color: 'inherit',
        onClick: () => {},
      },
    ],
  };
  const [confirmDialogData, setConfirmDialogData] = useState(defaultConfirmDialogData);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
  }, []);

  useEffect(() => {
    getActivities();
  }, [userId]);

  const getActivities = () => {
    if (!userId) return;

    otherActivityReportService.getData({ userId }).then((data) => {
      setActivities(data);
    });
  };

  const hideConfirmDialog = () => {
    setConfirmDialogData(defaultConfirmDialogData);
  };

  const checkOpenReportByUser = ({ onConfirm }) => {
    hideConfirmDialog();
    otherActivityReportService.checkOpenReportByUser(userId).then((data) => {
      if (data.hasOpenReport) {
        setConfirmDialogData({
          open: true,
          message: 'Már van folyamatban lévő munkád. Megszakítod azt, hogy elindíthasd ezt?',
          buttons: [
            {
              label: 'Igen',
              variant: 'contained',
              color: '#27ca37',
              onClick: onConfirm,
            },
          ],
        });
      } else {
        onConfirm();
      }
    });
  };

  const handleStartJob = (data) => {
    const body = {
      otherActivityId: data.id,
      userId,
    };

    otherActivityReportService.startJob(body).then(() => {
      getActivities();
      hideConfirmDialog();

      if (dialog) {
        dialog.setRefetch(true);
      }
    });
  };

  const handleStopJob = (data) => {
    const body = {
      id: data.openReport.id,
      userId,
    };

    otherActivityReportService.stopJob(body).then(() => {
      getActivities();
      hideConfirmDialog();
    });
  };

  const openConfirmDialog = (data) => {
    if (data.openReport) {
      setConfirmDialogData({
        open: true,
        message: `Biztosan leállítja a ${data.name} tevékenységet?`,
        buttons: [
          {
            label: 'Igen',
            variant: 'contained',
            color: '#27ca37',
            onClick: () => handleStopJob(data),
          },
        ],
      });
    } else {
      setConfirmDialogData({
        open: true,
        message: `Biztosan el szeretné indítani a ${data.name} tevékenységet?`,
        buttons: [
          {
            label: 'Igen',
            variant: 'contained',
            color: '#27ca37',
            onClick: () => {
              checkOpenReportByUser({
                onConfirm: () => handleStartJob(data),
              });
            },
          },
        ],
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          ...sxConfig.page,
          minHeight: dialog ? '100%' : 'calc(100vh - 65px)',
          backgroundColor: dialog
            ? '#fff'
            : {
                xs: '#fff',
                md: '#e9f3fc',
              },
        }}
      >
        <Grid
          container
          spacing={{
            xs: 1.5,
            md: 2.5,
          }}
          justifyContent="center"
        >
          {activities?.map((activity) => {
            const hasOpenReport = !!activity.openReport;

            return (
              <Grid item key={activity.id} sx={sxConfig.cardGridItem}>
                <Box
                  sx={{
                    ...sxConfig.card,
                    border: {
                      xs: `2px solid ${hasOpenReport ? '#1E88E5' : '#BFE9D2'}`,
                      md: `4px solid ${hasOpenReport ? '#1E88E5' : '#16a34a'}`,
                    },
                    backgroundColor: hasOpenReport
                      ? '#56A5F7'
                      : {
                          xs: '#effaf3',
                          md: '#d8f4f8',
                        },
                    color: hasOpenReport
                      ? 'white'
                      : {
                          xs: '#000',
                          md: '#16a34a',
                        },
                    '@media (hover: hover)': {
                      '&:hover': {
                        backgroundColor: !hasOpenReport && '#bbf7d0',
                        transform: 'translateY(-4px) scale(1.05)',
                      },
                    },
                  }}
                  onClick={() => openConfirmDialog(activity)}
                >
                  <Typography component="div" sx={sxConfig.activityTitle}>
                    {activity.name}
                  </Typography>

                  <IconButton disableRipple tabIndex={-1} sx={sxConfig.activityIconButton}>
                    {getIcon(
                      activity.icon,
                      isLgUp ? '3rem' : '2.5rem',
                      hasOpenReport ? 'white' : isLgUp ? '#16a34a' : '#0fbd1d'
                    )}
                  </IconButton>

                  {hasOpenReport && (
                    <Box sx={sxConfig.openReportTimer}>
                      <OpenReportTimer startDate={activity.openReport.startDate} />
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <ConfirmDialogV2UI
        {...confirmDialogData}
        largeButtons={true}
        maxWidth="lg"
        fullWidth={true}
        onClose={hideConfirmDialog}
      />
    </>
  );
};
